import React, { useMemo } from 'react';

import MapPointer from '@ast/magma/components/icon/icons/MapPointer';

import { FieldInfoGroupField } from '@app/common/configurable-wizards';
import { FieldComponent, FieldLocator } from '@app/common/configurable-wizards/types';
import { isFieldInfoGroupField } from '@app/common/configurable-wizards/utils';

import { t } from '@app/widgets/contacts-and-settings/hooks/useUserSettingsTranslation';
import { formatAddress } from '@app/widgets/contacts-and-settings/utils/labelFormatters';
import { getMappedFields } from '@app/widgets/contacts-and-settings/utils/subFieldsMapper';

import { AddressItemFields, AddressTypes } from '../../types';

import styles from './AddressFieldsListItem.pcss';

const addressTypeLabels = {
  [AddressTypes.Physical]: t(
    'address-field-list-item.address-type.physical|Label for physical address type',
    'Physical address',
  ),
  [AddressTypes.Mailing]: t(
    'address-field-list-item.address-type.mailing|Label for mailing address type',
    'Mailing address',
  ),
  [AddressTypes.MilitaryMailing]: t(
    'address-field-list-item.address-type.military-mailing|Label for military mailing address type',
    'Military mailing address',
  ),
};

export const AddressFieldsListItem: FieldComponent<FieldInfoGroupField> = ({ field }) => {
  const fieldMeta = useMemo(
    () => getMappedFields(field.subFields),
    [field],
  );
  const label = useMemo(() => formatAddress(fieldMeta), [fieldMeta]);

  return (
    <div
      data-stable-name={`${fieldMeta.Type?.value}AddressItem`}
      className={styles.listItem}
    >
      <MapPointer className={styles.icon} />
      <div className={styles.details}>
        <div>{label}</div>
        <div className={styles.addressType}>
          {addressTypeLabels[AddressTypes[fieldMeta.Type?.value as keyof typeof AddressTypes]]}
        </div>
      </div>
    </div>
  );
};

export const AddressFieldsListItemLocator: FieldLocator = ({ field }) => (
  isFieldInfoGroupField(field) && field.id in AddressItemFields
    ? AddressFieldsListItem
    : undefined
);
